import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Pause,
  Music,
  Mic,
  MicOff,
  Bed,
  AlarmClock,
  BellRing,
  Check,
} from "lucide-react";
import { sleepAidTracks } from "../mock";
import { useMic } from "../context/MicContext";
import ScrollPicker from "../components/ScrollPicker";
import { scheduleAlarm, cancelAlarm } from "../services/alarmService";
import { playAmbient, stopCurrent } from "../services/ambientAudio";

function Star({ style }) {
  return (
    <div
      className="absolute rounded-full bg-white"
      style={{
        ...style,
        boxShadow: "0 0 6px rgba(255,255,255,0.9)",
        animation: `twinkle ${2 + Math.random() * 3}s ease-in-out ${
          Math.random() * 3
        }s infinite`,
      }}
    />
  );
}

export default function Bedtime() {
  const navigate = useNavigate();
  const mic = useMic();
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  // Unico suono usato sia come sleep aid che come suono della sveglia.
  const [track, setTrack] = useState(sleepAidTracks[0]);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [alarmStatus, setAlarmStatus] = useState(""); // toast inline

  useEffect(() => {
    if (!started) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [started]);

  const elapsedLabel = useMemo(() => {
    const h = Math.floor(elapsed / 3600);
    const m = Math.floor((elapsed % 3600) / 60);
    const s = elapsed % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [elapsed]);

  const stars = useMemo(
    () =>
      Array.from({ length: 60 }, () => ({
        top: `${Math.random() * 70}%`,
        left: `${Math.random() * 100}%`,
        width: `${1 + Math.random() * 2.2}px`,
        height: `${1 + Math.random() * 2.2}px`,
        opacity: 0.4 + Math.random() * 0.6,
      })),
    []
  );

  const requestMicIfNeeded = async () => {
    if (mic.permission !== "granted") {
      await mic.request();
    }
  };

  return (
    <div
      className="min-h-screen flex justify-center relative overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #1a0a1f 0%, #221133 15%, #152439 40%, #0f2b3a 70%, #0a3b3a 100%)",
      }}
    >
      {/* starfield */}
      <div className="absolute inset-0 pointer-events-none">
        {stars.map((s, i) => (
          <Star key={i} style={s} />
        ))}
      </div>

      <div className="relative w-full max-w-[430px] min-h-screen flex flex-col text-white">
        {/* top bar */}
        <div className="h-14 px-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 flex items-center justify-center transition-colors"
            aria-label="indietro"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="text-[13px] uppercase tracking-[0.3em] text-white/80">
            Addormentamento
          </div>
          <div className="w-10" />
        </div>

        {/* Selettore suono unico: usato come sleep aid E come suono sveglia */}
        <div className="px-4 mt-2">
          <button
            onClick={() => setPickerOpen((v) => !v)}
            data-testid="sound-picker-btn"
            className="w-full h-12 rounded-full border border-white/30 bg-white/5 backdrop-blur-sm flex items-center justify-between px-4 hover:bg-white/10 transition-colors"
          >
            <div className="flex items-center gap-3 text-white/90">
              <Music size={16} />
              <span className="text-[15px]">{track.name}</span>
              <span className="hidden xs:inline text-[10.5px] uppercase tracking-wider text-[#67e8d1]/85">
                Sleep aid + sveglia
              </span>
            </div>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (playing) {
                  await stopCurrent();
                  setPlaying(false);
                } else {
                  const ok = await playAmbient(track.id);
                  if (ok) setPlaying(true);
                }
              }}
              className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
              aria-label="play"
              data-testid="sound-preview-btn"
            >
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
          </button>

          {pickerOpen && (
            <div
              className="mt-2 rounded-2xl border border-white/15 bg-[#0a2030]/80 backdrop-blur-md overflow-hidden animate-[fadeUp_0.25s_ease-out]"
              data-testid="sound-picker-list"
            >
              {sleepAidTracks.map((t) => (
                <button
                  key={t.id}
                  onClick={async () => {
                    setTrack(t);
                    setPickerOpen(false);
                    if (playing) {
                      const ok = await playAmbient(t.id);
                      if (!ok) setPlaying(false);
                    }
                  }}
                  data-testid={`sound-option-${t.id}`}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left text-sm transition-colors ${
                    track.id === t.id
                      ? "bg-[#14c2a1]/15 text-white"
                      : "text-white/80 hover:bg-white/5"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {track.id === t.id && (
                      <Check size={14} className="text-[#67e8d1]" />
                    )}
                    {t.name}
                  </span>
                  <span className="text-white/50 text-[11px] flex items-center gap-1">
                    <BellRing size={11} className="text-[#67e8d1]/70" /> Sveglia
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* clock / picker */}
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          {!started ? (
            <>
              <div
                className="flex items-center gap-6 mb-8"
                data-testid="bedtime-time-picker"
              >
                <ScrollPicker
                  values={Array.from({ length: 24 }, (_, i) => i)}
                  value={hour}
                  onChange={setHour}
                  ariaLabel="ore"
                />
                <div className="text-white text-[56px] leading-none -mt-1 font-light">
                  :
                </div>
                <ScrollPicker
                  values={Array.from({ length: 12 }, (_, i) => i * 5)}
                  value={minute}
                  onChange={setMinute}
                  ariaLabel="minuti"
                />
              </div>
            </>
          ) : (
            <>
              <div
                className="relative w-[240px] h-[240px] rounded-full flex items-center justify-center"
                style={{
                  background:
                    "radial-gradient(circle at 50% 50%, rgba(20,194,161,0.25), rgba(20,194,161,0) 70%)",
                }}
              >
                <div
                  className="absolute inset-8 rounded-full border border-white/20"
                  style={{ animation: "pulseRing 3s ease-out infinite" }}
                />
                <div
                  className="absolute inset-4 rounded-full border border-white/10"
                  style={{ animation: "pulseRing 3s 0.8s ease-out infinite" }}
                />
                <div className="relative text-center">
                  <div className="text-white/70 text-xs uppercase tracking-[0.3em] mb-2">
                    Monitoraggio
                  </div>
                  <div className="text-white text-4xl font-light tracking-tight font-mono">
                    {elapsedLabel}
                  </div>
                  <div className="mt-2 text-white/70 text-[13px]">
                    Sveglia: {String(hour).padStart(2, "0")}:
                    {String(minute).padStart(2, "0")}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center gap-2 text-white/70 text-[12px]">
                <div className="flex items-center gap-2">
                  {mic.permission === "granted" ? (
                    <>
                      <Mic size={14} className="text-[#14c2a1]" />
                      <span>Microfono attivo · rilevamento suoni</span>
                    </>
                  ) : (
                    <>
                      <MicOff size={14} />
                      <span>Microfono non attivo</span>
                    </>
                  )}
                </div>
                {alarmStatus && (
                  <div
                    className="flex items-center gap-2 text-[#67e8d1]"
                    data-testid="alarm-status"
                  >
                    <BellRing size={12} />
                    <span>{alarmStatus}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* start / stop */}
        <div className="px-6 pb-10">
          {!started ? (
            <button
              onClick={async () => {
                await requestMicIfNeeded();
                mic.startMonitoring();
                // Pianifica la sveglia su iOS/Android (no-op su web)
                const res = await scheduleAlarm({
                  hour,
                  minute,
                  alarmSoundId: track.id,
                  title: "Sveglia SmartDream",
                  body: `Buongiorno! Ore ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
                });
                if (res.scheduled) {
                  setAlarmStatus(
                    `Sveglia attiva alle ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} · ${track.name}`
                  );
                } else if (res.reason === "no_permission") {
                  setAlarmStatus(
                    "Permesso notifiche negato — la sveglia non suonerà"
                  );
                } else if (res.reason === "not_native") {
                  setAlarmStatus("Sveglia attiva (web preview)");
                } else {
                  setAlarmStatus("Errore pianificazione sveglia");
                }
                setStarted(true);
                setElapsed(0);
              }}
              data-testid="bedtime-start-btn"
              className="w-full h-14 rounded-full border border-white/40 bg-white/5 text-white text-[18px] font-light tracking-wider hover:bg-white/10 transition-colors flex items-center justify-center gap-3"
            >
              <Bed size={18} /> Avvia
            </button>
          ) : (
            <button
              onClick={async () => {
                setStarted(false);
                mic.stopMonitoring();
                await stopCurrent();
                await cancelAlarm();
                setPlaying(false);
                setAlarmStatus("");
                navigate("/riepilogo");
              }}
              data-testid="bedtime-stop-btn"
              className="w-full h-14 rounded-full bg-white text-[#0a2030] text-[18px] font-medium tracking-wide hover:bg-white/90 transition-colors flex items-center justify-center gap-3"
            >
              <AlarmClock size={18} /> Termina e vedi il riepilogo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
